import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Mail,
  BellOff,
} from "lucide-react";

import "./SponsorRegistry.css";

const TAGS = [
  "AI",
  "Capital",
  "Climate",
  "Craft",
  "Design",
  "Founders",
  "Policy",
  "Research",
  "Scale",
];
const TIERS = ["Table Host", "Circle Patron", "Founding Partner"];

export default function SponsorRegistry() {
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    website: "",
    value_prop: "",
    interests: [],
    tiers_interested: [],
    notes: "",
    agree_terms: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email) {
      return toast.error("Company, name and email are required.");
    }
    setSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 800));

    try {
      setSubmitted(true);
      toast.success(
        "You're on the registry. Check your inbox for confirmation.",
      );
    } catch {
      toast.error("Could not join the registry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="container-x sponsorRegSuccess"
        data-testid="sponsor-registry-success"
      >
        <CheckCircle2 className="sponsorRegSuccessIcon" size={28} />
        <p className="overline sponsorRegSuccessOverline">
          You're on the registry
        </p>
        <h1 className="font-display sponsorRegSuccessTitle">
          We'll only email when there's a real match.
        </h1>
        <p className="sponsorRegSuccessBody">
          When a vmeetio event in your topics opens a sponsor slot within 48
          hours, you'll receive a short note with a one-click sponsor button.
          Every email includes an unsubscribe and "manage preferences" link — no
          logins, no spam.
        </p>
        <div className="sponsorRegSuccessCtas">
          <Link to="/" className="btn-gold" data-testid="registry-success-home">
            Back to vmeetio <ArrowRight size={16} />
          </Link>
          <Link to="/sponsor" className="btn-ghost">
            Or sponsor a live event now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sponsor-registry-page" className="sponsorRegPage">
      <section
        className="sponsorRegHero"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="container-x sponsorRegHeroInner">
          <Link to="/sponsor" className="sponsorRegBackLink">
            <ArrowLeft size={12} /> Back to sponsorship
          </Link>

          <p className="overline sponsorRegHeroOverline">
            <Sparkles size={12} /> Sponsor registry
          </p>

          <h1 className="font-display sponsorRegHeroTitle">
            Hear about every sponsor slot —<br />
            <span className="sponsorRegHeroTitleAccent">
              before the event is sold out.
            </span>
          </h1>

          <p className="sponsorRegHeroBody">
            Join the registry once. When a vmeetio event in your interest areas
            has an open sponsor slot within 48 hours, we'll send you a short
            note with a one-click <em>Claim this slot</em> button. Daily sweep,
            opt-out anytime.
          </p>
        </div>
      </section>

      <section className="section sponsorRegBody">
        <div className="container-x sponsorRegGrid">
          <form
            onSubmit={onSubmit}
            className="glass sponsorRegForm"
            data-testid="sponsor-registry-form"
          >
            <p className="overline sponsorRegFormOverline">Your company</p>

            <div className="sponsorRegFormGrid2">
              <Field
                label="Company"
                value={form.company}
                onChange={(v) => set("company", v)}
                testid="registry-company"
                required
              />
              <Field
                label="Contact name"
                value={form.contact_name}
                onChange={(v) => set("contact_name", v)}
                testid="registry-contact-name"
                required
              />
              <Field
                label="Work email"
                type="email"
                value={form.email}
                onChange={(v) => set("email", v)}
                testid="registry-email"
                required
              />
              <Field
                label="Website"
                value={form.website}
                onChange={(v) => set("website", v)}
                testid="registry-website"
                placeholder="https://…"
              />
            </div>

            <Field
              label="One-line value prop"
              value={form.value_prop}
              onChange={(v) => set("value_prop", v)}
              testid="registry-value-prop"
              placeholder="What you'd say to a CEO at a dinner."
            />

            <div className="sponsorRegBlock">
              <p className="overline sponsorRegBlockTitle">
                Interests · pick any that fit
              </p>
              <div className="sponsorRegPills">
                {TAGS.map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => toggle("interests", t)}
                    data-testid={`registry-tag-${t}`}
                    className={`sponsorRegPill ${
                      form.interests.includes(t)
                        ? "isActiveGold"
                        : "isInactiveGold"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <p className="sponsorRegHint">
                Leave empty to be notified about every open slot.
              </p>
            </div>

            <div className="sponsorRegBlock">
              <p className="overline sponsorRegBlockTitle">Open to tiers</p>
              <div className="sponsorRegPills">
                {TIERS.map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => toggle("tiers_interested", t)}
                    data-testid={`registry-tier-${t}`}
                    className={`sponsorRegPill ${
                      form.tiers_interested.includes(t)
                        ? "isActiveDarkGold"
                        : "isInactiveTier"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <Field
              label="Notes for our team (optional)"
              textarea
              rows={3}
              value={form.notes}
              onChange={(v) => set("notes", v)}
              testid="registry-notes"
            />

            <label className="sponsorRegTermsRow">
              <input
                type="checkbox"
                checked={form.agree_terms}
                onChange={(e) => set("agree_terms", e.target.checked)}
                data-testid="registry-agree"
                className="sponsorRegCheckbox"
              />
              <span>
                I agree to receive sponsor-opportunity emails from vmeetio. I
                can unsubscribe at any time with one click — every email
                includes an unsubscribe and preference-management link.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting || !form.agree_terms}
              data-testid="registry-submit"
              className="btn-gold sponsorRegSubmit"
            >
              {submitting ? (
                "Joining…"
              ) : (
                <>
                  Join the registry <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <aside className="sponsorRegAside">
            <div className="glass sponsorRegAsideCard">
              <Mail className="sponsorRegAsideIcon" size={18} />
              <h3 className="font-display sponsorRegAsideTitle">
                How it works
              </h3>
              <ol className="sponsorRegHowList">
                <li>
                  Tell us your topics + which sponsor tiers you'd consider.
                </li>
                <li>
                  A daily sweep matches your interests against events ≤ 48 hrs
                  out that still have an open sponsor slot.
                </li>
                <li>
                  If a match exists we send <em>one</em> email per event. Never
                  duplicated.
                </li>
                <li>
                  One-click "Claim this slot" → goes straight to the sponsor
                  form pre-filled with the event.
                </li>
              </ol>
            </div>

            <div className="glass sponsorRegAsideCard">
              <BellOff className="sponsorRegAsideIcon" size={18} />
              <h3 className="font-display sponsorRegAsideTitle">
                Always opt-out
              </h3>
              <p className="sponsorRegAsideBody">
                Every notification has a one-click unsubscribe + a preferences
                page where you can mute specific topics or pause for 7 / 30 / 90
                days. No login required.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  textarea,
  testid,
  ...rest
}) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <label className="sponsorRegField">
      <span className="overline sponsorRegLabel">{label}</span>
      <Comp
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="sponsorRegInput"
        {...rest}
      />
    </label>
  );
}
